import { X } from "lucide-react";
import React, { ReactNode } from "react";

import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  Grid,
  IconButton,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";

import { IgButton } from "./IgButton";
import { IgIconButton } from "./IgIconButton";
import { IgTooltip } from "./IgTooltip";
import type { IgCustomTheme } from "./theme";

const StyledDialog = styled(Dialog)<{ theme?: IgCustomTheme }>(({ theme }) => ({
  "&.MuiDialog-root": {
    marginLeft: "0 !important",
  },
}));

interface IgFullPageDialogProps
  extends Omit<DialogProps, "open" | "fullScreen" | "content"> {
  showDialog: boolean;
  handleClose: (event?: any, reason?: any) => void;
  content: ReactNode;
  header?: ReactNode;
  noButtonText?: string | null;
  handleNoClick?: () => void;
  hideCancelButton?: boolean;
  yesButtonText?: string;
  handleYesClick?: () => void;
  yesButtonDisabled?: boolean;
  yesButtonDisabledTooltip?: string;
  hideActions?: boolean;
  hideYesButton?: boolean;
  shouldNotClose?: boolean;
  leftButtonHidden?: boolean;
  leftButtonText?: string;
  onLeftButtonClick?: () => void;
}

export const IgFullPageDialog = ({
  showDialog,
  handleClose,
  content,
  header,
  noButtonText,
  handleNoClick,
  hideCancelButton = false,
  yesButtonText,
  handleYesClick,
  yesButtonDisabled = false,
  yesButtonDisabledTooltip,
  hideActions = false,
  hideYesButton = false,
  shouldNotClose,
  leftButtonHidden = true,
  leftButtonText,
  onLeftButtonClick,
  ...rest
}: IgFullPageDialogProps) => {
  const theme = useTheme<IgCustomTheme>();
  const onYesClickPress = () => {
    if (handleYesClick) {
      handleYesClick();
    }
    if (!shouldNotClose) {
      handleClose();
    }
  };

  const onNoClickPress = () => {
    if (handleNoClick) {
      handleNoClick();
    } else {
      handleClose();
    }
  };

  return (
    <StyledDialog open={showDialog} onClose={handleClose} fullScreen {...rest}>
      {!hideCancelButton && (
        <Box
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            zIndex: 1,
          }}
        >
          <IgIconButton onClick={handleClose}>
            <X size={24} />
          </IgIconButton>
        </Box>
      )}

      {header && (
        <Box
          sx={{
            padding: "12px 68px 12px 16px",
            backgroundColor: theme.igBackgroundColors.baseLightMid,
            borderBottom: `1px solid ${theme.igBorders.core.default}`,
          }}
        >
          {header}
        </Box>
      )}

      <DialogContent
        sx={{
          padding: 8,
          paddingBottom: hideActions ? undefined : 0,
          backgroundColor: theme.igBackgroundColors.baseLightLow,
        }}
      >
        {content}
      </DialogContent>

      {!hideActions && (
        <DialogActions
          sx={{
            padding: "16px 32px 16px 32px",
            backgroundColor: theme.palette.common.white,
            border: `1px solid ${theme.igBorders.core.default}`,
          }}
        >
          <Grid container item justifyContent={"space-between"} wrap={"nowrap"}>
            {!leftButtonHidden && (
              <Grid item>
                <IgButton
                  variant={"outlined"}
                  color={"secondary"}
                  onClick={onLeftButtonClick}
                  sx={{ width: "max-content", paddingX: 8 }}
                >
                  {leftButtonText || "Back"}
                </IgButton>
              </Grid>
            )}
            <Grid container item justifyContent="end" direction="row">
              {noButtonText !== null && (
                <Grid item marginRight={3}>
                  <IgButton
                    variant="contained"
                    color="secondary"
                    sx={{ minWidth: "112px" }}
                    onClick={onNoClickPress}
                  >
                    {noButtonText || "Cancel"}
                  </IgButton>
                </Grid>
              )}
              {!hideYesButton && (
                <Grid item>
                  <IgTooltip title={yesButtonDisabledTooltip || ""}>
                    <IgButton
                      variant="contained"
                      color="primary"
                      sx={{ minWidth: "96px" }}
                      disabled={yesButtonDisabled}
                      onClick={onYesClickPress}
                    >
                      {yesButtonText || "Next"}
                    </IgButton>
                  </IgTooltip>
                </Grid>
              )}
            </Grid>
          </Grid>
        </DialogActions>
      )}
    </StyledDialog>
  );
};
